import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { AccordionModule } from 'primeng/accordion';
import { DrawerModule } from 'primeng/drawer';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import moment from 'moment';
import { getSvgIcon } from '../utils/svg-icons.utils';
import { ColumnPreferenceService } from '../services/column-preference/column-preference.service';
import {
  UploadApiService,
  UploadJobResponse,
  UploadJobDetailResponse,
  UploadValidationError
} from '../services/upload/upload-api.service';
import { ToasterService } from '@ui-nuvertos/auth';

interface ColumnConfig {
  key: string;
  header: string;
  width?: string;
  align?: string;
}

@Component({
  selector: 'lib-uploads-list',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    BadgeModule,
    ProgressBarModule,
    TooltipModule,
    DialogModule,
    CheckboxModule,
    AccordionModule,
    DragDropModule,
    DrawerModule,
    FormsModule
  ],
  providers: [],
  templateUrl: './uploads-list.component.html',
  styleUrls: ['./uploads-list.component.css']
})
export class UploadsListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private uploadApiService = inject(UploadApiService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private columnPreferenceService = inject(ColumnPreferenceService);

  isLoading = false;
  uploads: UploadJobResponse[] = [];
  selectedUploads: UploadJobResponse[] = [];
  totalCount = 0;
  pageIndex = 1;
  rowsPerPage = 10;

  isAnalysisExpanded = true;
  displayFilterSidebar: boolean = false;
  
  // Custom delete dialog state
  displayDeleteDialog: boolean = false;
  jobToDelete: UploadJobResponse | null = null;
  deleteActionType: 'cancel' | 'delete' = 'delete';

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.rowsPerPage);
  }
  showErrorsDialog = false;
  selectedJobErrors: UploadValidationError[] = [];
  selectedJobForErrors: UploadJobDetailResponse | null = null;
  isLoadingErrors = false;

  // Auto-refresh interval in ms (20 seconds to reduce server load)
  private autoRefreshInterval = 20000;
  private hasActiveJobs = false;

  selectedColumns: string[] = [];
  orderedColumns: ColumnConfig[] = [];

  columns: ColumnConfig[] = [
    { key: 'sno', header: 'S.No.', width: '5rem' },
    { key: 'segment', header: 'Segment', width: '10rem' },
    { key: 'filename', header: 'File Name' },
    { key: 'size', header: 'Size', width: '7rem' },
    { key: 'created_date', header: 'Created Date', width: '11rem' },
    { key: 'mode', header: 'Mode', width: '8rem' },
    { key: 'results', header: 'Results', width: '10rem' },
    { key: 'status', header: 'Status', width: '14rem' }
  ];

  ngOnInit(): void {
    this.orderedColumns = [...this.columns];
    this.selectedColumns = this.columns.map(c => c.key);
    this.loadUploads();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadUploads(): Promise<void> {
    try {
      this.isLoading = true;

      const response = await this.uploadApiService.listUploadJobs({
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage
      }).toPromise();

      if (response?.data) {
        this.uploads = response.data.items || [];
        this.totalCount = response.data.total || 0;

        // Check if there are active jobs needing refresh
        this.hasActiveJobs = this.uploads.some(
          job => job.status === 'pending' || job.status === 'processing'
        );
      }
    } catch (error) {
      console.error('Error loading uploads:', error);
      this.toasterService.error('Error', 'Failed to load uploads');
    } finally {
      this.isLoading = false;
    }
  }

  private startAutoRefresh(): void {
    interval(this.autoRefreshInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Only refresh if there are active jobs
        if (this.hasActiveJobs) {
          this.loadUploads();
        }
      });
  }

  async downloadErrorReport(job: UploadJobResponse): Promise<void> {
    if (!job.has_error_report) {
      this.toasterService.warn('Warning', 'No error report available');
      return;
    }

    try {
      await this.uploadApiService.downloadErrorReport(job.id);
      this.toasterService.success('Success', 'Download started');
    } catch (error) {
      console.error('Error downloading error report:', error);
      this.toasterService.error('Error', 'Failed to download error report');
    }
  }

  confirmDelete(job: UploadJobResponse): void {
    this.jobToDelete = job;
    this.deleteActionType = job.status === 'pending' || job.status === 'processing' 
      ? 'cancel' 
      : 'delete';
    this.displayDeleteDialog = true;
  }

  cancelDelete(): void {
    this.displayDeleteDialog = false;
    this.jobToDelete = null;
  }

  async executeDelete(): Promise<void> {
    if (!this.jobToDelete) return;
    const job = this.jobToDelete;
    this.displayDeleteDialog = false;
    this.jobToDelete = null;
    
    try {
      await this.uploadApiService.deleteUploadJob(job.id).toPromise();
      this.toasterService.success('Success', `Upload job ${this.deleteActionType}d`);
      this.loadUploads();
    } catch (error) {
      console.error('Error deleting upload:', error);
      this.toasterService.error('Error', `Failed to ${this.deleteActionType} upload job`);
    }
  }

  loadPreviousPage(): void {
    if (this.pageIndex > 1) {
      this.pageIndex--;
      this.loadUploads();
    }
  }

  loadNextPage(): void {
    if (this.pageIndex < this.totalPages) {
      this.pageIndex++;
      this.loadUploads();
    }
  }

  async viewErrors(job: UploadJobResponse): Promise<void> {
    try {
      this.isLoadingErrors = true;
      this.selectedJobForErrors = job as UploadJobDetailResponse;
      this.showErrorsDialog = true;

      const response = await this.uploadApiService.getUploadJob(job.id).toPromise();
      if (response?.data) {
        // Update selected job with full details (including error_message)
        this.selectedJobForErrors = response.data;
        this.selectedJobErrors = response.data.validation_errors || [];
      } else {
        this.selectedJobErrors = [];
      }
    } catch (error) {
      console.error('Error fetching validation errors:', error);
      this.toasterService.error('Error', 'Failed to load validation errors');
      this.selectedJobErrors = [];
    } finally {
      this.isLoadingErrors = false;
    }
  }

  closeErrorsDialog(): void {
    this.showErrorsDialog = false;
    this.selectedJobErrors = [];
    this.selectedJobForErrors = null;
  }

  async retryUpload(job: UploadJobResponse): Promise<void> {
    try {
      await this.uploadApiService.retryUploadJob(job.id).toPromise();
      this.toasterService.success('Success', 'Upload job queued for retry');
      this.loadUploads();
    } catch (error: any) {
      console.error('Error retrying upload:', error);
      this.toasterService.error('Error', error?.error?.message || 'Failed to retry upload job');
    }
  }

  canRetry(job: UploadJobResponse): boolean {
    return job.status === 'failed';
  }

  canViewErrors(job: UploadJobResponse): boolean {
    return job.error_count > 0 || job.status === 'failed' || job.status === 'partial';
  }

  onPageChange(event: any): void {
    this.pageIndex = Math.floor(event.first / event.rows) + 1;
    this.rowsPerPage = event.rows;
    this.loadUploads();
  }

  refreshList(): void {
    this.loadUploads();
  }

  // Helper methods using the service
  getModuleLabel(module: string): string {
    return this.uploadApiService.getModuleLabel(module);
  }

  getStatusLabel(status: string): string {
    return this.uploadApiService.getStatusLabel(status);
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    return this.uploadApiService.getStatusSeverity(status);
  }

  formatFileSize(bytes?: number): string {
    return this.uploadApiService.formatFileSize(bytes);
  }

  formatDate(dateStr?: string): string {
    if (!dateStr) return '-';
    return moment(dateStr).format('DD/MM/YYYY HH:mm');
  }

  getTimeAgo(dateStr?: string): string {
    if (!dateStr) return '';
    return moment(dateStr).fromNow();
  }

  getModeLabel(mode: string): string {
    return this.uploadApiService.getModeLabel(mode);
  }

  canDownloadErrorReport(job: UploadJobResponse): boolean {
    return job.has_error_report && (job.status === 'partial' || job.status === 'failed' || job.error_count > 0);
  }

  canDelete(job: UploadJobResponse): boolean {
    // Cannot delete processing jobs
    return job.status !== 'processing';
  }

  isProcessing(job: UploadJobResponse): boolean {
    return job.status === 'pending' || job.status === 'processing';
  }

  isCompleted(job: UploadJobResponse): boolean {
    return job.status === 'completed' || job.status === 'partial';
  }

  isFailed(job: UploadJobResponse): boolean {
    return job.status === 'failed';
  }

  // Stats helpers
  getActiveJobsCount(): number {
    return this.uploads.filter(job =>
      job.status === 'pending' || job.status === 'processing'
    ).length;
  }

  getCompletedJobsCount(): number {
    return this.uploads.filter(job => job.status === 'completed').length;
  }

  getPartialJobsCount(): number {
    return this.uploads.filter(job => job.status === 'partial').length;
  }

  getFailedJobsCount(): number {
    return this.uploads.filter(job => job.status === 'failed').length;
  }

  // Get result summary text
  getResultSummary(job: UploadJobResponse): string {
    const parts = [];
    if (job.success_count > 0) parts.push(`${job.success_count} created`);
    if (job.updated_count > 0) parts.push(`${job.updated_count} updated`);
    if (job.error_count > 0) parts.push(`${job.error_count} errors`);
    return parts.join(', ') || '-';
  }

  // Serial number based on pagination
  getSerialNumber(rowIndex: number): number {
    return (this.pageIndex - 1) * this.rowsPerPage + rowIndex + 1;
  }

  // Result label for the Results column (plain text)
  getResultLabel(job: UploadJobResponse): string {
    if (this.isProcessing(job)) {
      return this.getResultSummary(job);
    }
    if (job.status === 'failed') {
      return job.error_count > 0 ? `${job.error_count} Error` : '1 Error';
    }
    if (job.status === 'partial') {
      const parts = [];
      if (job.success_count > 0) parts.push(`${job.success_count} Created`);
      if (job.error_count > 0) parts.push(`${job.error_count} Error`);
      return parts.join(', ') || '-';
    }
    if (job.status === 'completed') {
      const rows = job.success_count || job.updated_count || 0;
      return `${rows} Rows Created`;
    }
    return '-';
  }

  // Column Preference logic
  getSanitizedSvg(svgString: string | undefined): SafeHtml | null {
    if (!svgString) return null;
    return this.sanitizer.bypassSecurityTrustHtml(svgString);
  }

  getColumnPreferenceIcon(): SafeHtml | null {
    const icon = getSvgIcon('COLUMN_PREFERENCE');
    return this.getSanitizedSvg(icon);
  }

  openFilterSidebar() {
    this.displayFilterSidebar = true;
  }

  toggleColumnSelection(columnKey: string, isSelected: boolean) {
    if (isSelected) {
      if (!this.selectedColumns.includes(columnKey)) {
        const columnIndex = this.orderedColumns.findIndex(col => col.key === columnKey);
        const selectedBefore = this.orderedColumns
          .slice(0, columnIndex)
          .filter(col => this.selectedColumns.includes(col.key))
          .map(col => col.key);
        const selectedAfter = this.orderedColumns
          .slice(columnIndex + 1)
          .filter(col => this.selectedColumns.includes(col.key))
          .map(col => col.key);
        
        this.selectedColumns = [...selectedBefore, columnKey, ...selectedAfter];
      }
    } else {
      this.selectedColumns = this.selectedColumns.filter(key => key !== columnKey);
    }
  }

  isColumnSelected(columnKey: string): boolean {
    return this.selectedColumns.includes(columnKey);
  }

  getVisibleColumns(): ColumnConfig[] {
    const columnMap = new Map(this.columns.map(col => [col.key, col]));
    return this.selectedColumns
      .map(key => columnMap.get(key))
      .filter((col): col is ColumnConfig => col !== undefined);
  }

  getOrderedColumnsForPreference(): ColumnConfig[] {
    return this.orderedColumns;
  }

  dropColumn(event: CdkDragDrop<ColumnConfig[]>) {
    moveItemInArray(this.orderedColumns, event.previousIndex, event.currentIndex);
    const selectedInOrder = this.orderedColumns
      .filter(col => this.selectedColumns.includes(col.key))
      .map(col => col.key);
    this.selectedColumns = selectedInOrder;
  }

  clearColumnSelection() {
    this.selectedColumns = [];
    this.orderedColumns = [...this.columns];
  }

  applyColumnSelection() {
    this.displayFilterSidebar = false;
  }
}
